/**
 * ForeignKeySelect - Reusable component for foreign key dropdown fields
 *
 * Handles loading states, error states, and displays meaningful labels
 * for foreign key relationships in forms.
 */

import { memo } from "react"
import type { UseFormRegisterReturn } from "react-hook-form"

interface ForeignKeyOption {
  id: string
  [key: string]: any
}

interface ForeignKeySelectProps {
  /**
   * React Hook Form register return value
   */
  register: UseFormRegisterReturn

  /**
   * Array of options to display
   */
  options?: ForeignKeyOption[]

  /**
   * Loading state from the query
   */
  isLoading?: boolean

  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string

  /**
   * Whether the field has validation errors
   */
  hasError?: boolean

  /**
   * Whether the select is disabled
   */
  disabled?: boolean

  /**
   * Function to format the display label for each option
   * Receives the full option object and should return a string
   */
  formatLabel: (option: ForeignKeyOption) => string

  /**
   * Optional className for styling
   */
  className?: string
}

/**
 * Reusable select component for foreign key fields
 *
 * @example
 * // Basic usage with households
 * <ForeignKeySelect
 *   register={form.register('household_id')}
 *   options={householdsData?.data}
 *   isLoading={householdsLoading}
 *   placeholder="Select Household"
 *   hasError={!!form.formState.errors.household_id}
 *   formatLabel={(h) => `${h.parent_first_name} ${h.parent_last_name} - ${h.email}`}
 * />
 *
 * @example
 * // With students
 * <ForeignKeySelect
 *   register={form.register('student_id')}
 *   options={studentsData?.data}
 *   isLoading={studentsLoading}
 *   placeholder="Select Student"
 *   hasError={!!form.formState.errors.student_id}
 *   formatLabel={(s) => `${s.first_name} ${s.last_name}`}
 * />
 */
const ForeignKeySelect = memo(
  ({
    register,
    options = [],
    isLoading = false,
    placeholder = "Select an option",
    hasError = false,
    disabled = false,
    formatLabel,
    className = "chakra-select",
  }: ForeignKeySelectProps) => {
    return (
      <select
        {...register}
        aria-invalid={hasError}
        className={className}
        disabled={disabled || isLoading}
      >
        <option value="">{isLoading ? "Loading..." : placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {formatLabel(option)}
          </option>
        ))}
      </select>
    )
  },
)

ForeignKeySelect.displayName = "ForeignKeySelect"

export default ForeignKeySelect
export type { ForeignKeySelectProps, ForeignKeyOption }
